import React, { useState, useEffect } from 'react';
import {
    Container,
    Card,
    Row,
    Col,
    Badge,
    Button,
    Spinner,
    Alert,
    Table,
    Accordion,
} from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Cell,
} from 'recharts';
import surveyService from '@/shared/services/surveyService';
import { downloadBlob } from '@/shared/utils/downloadBlob';

const BAR_COLOR = '#1565C0';
const LIKERT_LABELS = {
    1: 'Strongly Disagree',
    2: 'Disagree',
    3: 'Neutral',
    4: 'Agree',
    5: 'Strongly Agree',
};

function SurveyResultsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        const loadResults = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await surveyService.getSurveyResults(id);
                setResults(data);
            } catch (err) {
                if (err.response?.status === 403) {
                    setError(
                        'You do not have permission to view these results.'
                    );
                } else {
                    setError(
                        'Failed to load survey results. Please try again.'
                    );
                }
            } finally {
                setLoading(false);
            }
        };

        loadResults();
    }, [id]);

    const handleExport = async () => {
        try {
            setExporting(true);
            const blob = await surveyService.exportResults(id);
            downloadBlob(blob, `survey-${id}-results.csv`);
        } catch (_err) {
            setError('Failed to export results. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    const renderLikertChart = (stat) => {
        const data = [1, 2, 3, 4, 5].map((value) => ({
            name: LIKERT_LABELS[value],
            value: stat.distribution?.[value] || 0,
            label: String(value),
        }));

        return (
            <div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">
                        Mean: <strong>{stat.mean || 'N/A'}</strong> | Responses:{' '}
                        <strong>{stat.responseCount}</strong>
                    </small>
                </div>
                <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="label" />
                        <YAxis allowDecimals={false} />
                        <Tooltip
                            formatter={(value, _name, props) => [
                                value,
                                props.payload.name,
                            ]}
                        />
                        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                            {data.map((_entry, index) => (
                                <Cell key={index} fill={BAR_COLOR} />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        );
    };

    const renderSelectTable = (stat) => {
        const frequency = stat.frequency || [];
        return (
            <Table striped size="sm" className="mb-0">
                <thead>
                    <tr>
                        <th>Option</th>
                        <th className="text-end">Count</th>
                        <th className="text-end">Percentage</th>
                    </tr>
                </thead>
                <tbody>
                    {frequency.map((item) => (
                        <tr key={item.option}>
                            <td>{item.option}</td>
                            <td className="text-end">{item.count}</td>
                            <td className="text-end">{item.percentage}%</td>
                        </tr>
                    ))}
                    {frequency.length === 0 && (
                        <tr>
                            <td colSpan={3} className="text-muted text-center">
                                No responses
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        );
    };

    const renderTextResponses = (stat) => {
        const textValues = (stat.values || []).filter(
            (v) => v && String(v).trim()
        );
        if (textValues.length === 0) {
            return (
                <p className="text-muted small mb-0">
                    No text responses received.
                </p>
            );
        }
        return (
            <Accordion flush>
                <Accordion.Item eventKey="0">
                    <Accordion.Header>
                        {textValues.length} response
                        {textValues.length !== 1 ? 's' : ''}
                    </Accordion.Header>
                    <Accordion.Body>
                        {textValues.map((val, idx) => (
                            <div
                                key={idx}
                                className={`py-2 ${idx < textValues.length - 1 ? 'border-bottom' : ''}`}
                            >
                                <p className="mb-0 small">{String(val)}</p>
                            </div>
                        ))}
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>
        );
    };

    const renderQuestionStat = (stat) => {
        return (
            <Card key={stat.questionId} className="mb-3 border-0 shadow-sm">
                <Card.Body>
                    <h4 className="mb-1 fs-6">{stat.text}</h4>
                    <div className="d-flex gap-2 mb-3">
                        <Badge bg="light" text="dark" className="border">
                            {stat.type}
                        </Badge>
                        <Badge bg="light" text="dark" className="border">
                            {stat.responseCount} response
                            {stat.responseCount !== 1 ? 's' : ''}
                        </Badge>
                    </div>

                    {(stat.type === 'likert' || stat.type === 'rating') &&
                        renderLikertChart(stat)}

                    {(stat.type === 'select' || stat.type === 'multiselect') &&
                        renderSelectTable(stat)}

                    {(stat.type === 'text' || stat.type === 'textarea') &&
                        renderTextResponses(stat)}

                    {stat.type === 'checkbox' && (
                        <div>
                            <p className="small mb-0">
                                Checked:{' '}
                                <strong>
                                    {
                                        (stat.values || []).filter(
                                            (v) => v === true
                                        ).length
                                    }
                                </strong>{' '}
                                | Unchecked:{' '}
                                <strong>
                                    {
                                        (stat.values || []).filter(
                                            (v) => v !== true
                                        ).length
                                    }
                                </strong>
                            </p>
                        </div>
                    )}
                </Card.Body>
            </Card>
        );
    };

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="mt-3 text-muted">Loading results...</p>
            </Container>
        );
    }

    if (!results) {
        return (
            <Container className="py-5">
                <Alert variant="danger">
                    {error || 'Failed to load survey results.'}
                </Alert>
            </Container>
        );
    }

    const { survey, totalResponses, questionStats } = results;

    return (
        <Container className="py-4">
            <div className="mb-4">
                <Button
                    variant="link"
                    className="p-0 text-muted"
                    onClick={() => navigate('/surveys')}
                >
                    &larr; Back to Surveys
                </Button>
            </div>

            {error && (
                <Alert
                    variant="danger"
                    dismissible
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            {/* Survey Header */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Row className="align-items-center">
                        <Col md={8}>
                            <h2 className="mb-1">{survey.title}</h2>
                            {survey.description && (
                                <p className="text-muted mb-2">
                                    {survey.description}
                                </p>
                            )}
                            <div className="d-flex gap-2">
                                <Badge bg="info" text="dark">
                                    {survey.targetAudience}
                                </Badge>
                                {survey.program && (
                                    <Badge bg="secondary">
                                        {survey.program.toUpperCase()}
                                    </Badge>
                                )}
                            </div>
                        </Col>
                        <Col md={4} className="text-md-end">
                            <div className="display-6 text-primary">
                                {totalResponses}
                            </div>
                            <small className="text-muted">
                                total response
                                {totalResponses !== 1 ? 's' : ''}
                            </small>
                            <div className="mt-2">
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={handleExport}
                                    disabled={exporting || totalResponses === 0}
                                >
                                    {exporting ? 'Exporting...' : 'Export CSV'}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Question Results */}
            {totalResponses === 0 ? (
                <Card className="border-0 bg-light">
                    <Card.Body className="text-center py-5">
                        <p className="text-muted mb-0">
                            No responses have been submitted for this survey
                            yet.
                        </p>
                    </Card.Body>
                </Card>
            ) : (
                <div>
                    <h3 className="mb-3 fs-5">Question Results</h3>
                    {(questionStats || []).map(renderQuestionStat)}
                </div>
            )}
        </Container>
    );
}

export default SurveyResultsPage;
